/**
 * ActionList Component
 *
 * Dropdown menu that portals to document.body to escape table cell overflow constraints.
 */

import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import classnames from 'classnames';

interface ActionListProps {
  title: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}

const ActionList: React.FC<ActionListProps> = ({
  title,
  className,
  children,
}) => {
  const [visible, setVisible] = useState(false);
  const [showPortal, setShowPortal] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [hasItems, setHasItems] = useState(true);
  const buttonRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Check if the list has any actual <li> items after rendering
  useEffect(() => {
    if (listRef.current) {
      const items = listRef.current.querySelectorAll('li');
      const hasAnyItems = items.length > 0;
      setHasItems(hasAnyItems);
    }
  }, [children]);

  // Calculate position and show portal after one frame
  useEffect(() => {
    if (!visible || !buttonRef.current) {
      setShowPortal(false);
      return;
    }

    const rect = buttonRef.current.getBoundingClientRect();
    const leftOffset = className?.includes('left') ? 0 : -70;

    setPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX + leftOffset,
    });

    // Delay showing portal by one frame to prevent flash from top-left
    requestAnimationFrame(() => {
      setShowPortal(true);
    });
  }, [visible, className]);

  // Close menu on resize
  useEffect(() => {
    if (!visible) return;

    const handleResize = () => setVisible(false);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [visible]);

  // Handle clicks outside
  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        portalRef.current &&
        !portalRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [visible]);

  return (
    <>
      <div
        ref={buttonRef}
        className={classnames('action-list', className, { visible })}
        onClick={() => setVisible(true)}
        style={{ display: hasItems ? undefined : 'none' }}
      >
        <span className="action-list-title">{title}</span>
      </div>
      {ReactDOM.createPortal(
        <div
          ref={portalRef}
          className="action-list-portal mt-10"
          style={{
            position: 'absolute',
            top: `${position.top}px`,
            left: `${position.left}px`,
            zIndex: 1,
            display: showPortal ? 'block' : 'none',
          }}
        >
          <ul
            ref={listRef}
            className="actions-list-items"
            onClick={e => {
              e.stopPropagation();
              setVisible(false);
            }}
          >
            {children}
          </ul>
        </div>,
        document.body
      )}
    </>
  );
};

export const Item = ({
  className,
  children,
}: Partial<Record<'className', string>> &
  Partial<Record<'children', React.ReactNode>>) => (
  <li className={classnames('action-list-item', className)}>{children}</li>
);

ActionList.Item = Item;
export default ActionList;
